import { Component } from '@theme/component';

const stationary = '[data-ov-motion="off"], [data-ov-review], [data-review], [data-reviews], .review, .reviews, .testimonial, .testimonials, .jdgm-widget, .loox-reviews-default, .spr-container, .yotpo, [class*="reviews"], [class*="testimonial"], [id*="reviews"], [id*="testimonial"]';

class OveluneMotion extends Component {
  #observer;
  #preference;
  #animations = new Set();
  #played = new WeakSet();

  connectedCallback() {
    super.connectedCallback();
    this.#start();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
    super.disconnectedCallback();
  }

  #onPreferenceChange = () => this.#start();

  #start() {
    this.#stop();
    if (this.dataset.ovMotionEnabled === 'false' || this.closest(stationary)) return;
    if (!('IntersectionObserver' in window)) return;
    this.#preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#preference.addEventListener('change', this.#onPreferenceChange);
    if (this.#preference.matches) return;

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.#reveal(entry.target);
      }
    }, { threshold: 0.12, rootMargin: '0px 0px -24px 0px' });

    for (const element of this.querySelectorAll('[data-ov-reveal]')) {
      if (!element.closest(stationary) && !this.#played.has(element)) this.#observer.observe(element);
    }
  }

  #reveal(element) {
    this.#observer?.unobserve(element);
    if (!this.isConnected || this.#played.has(element) || element.closest(stationary)) return;
    if (this.#preference?.matches || typeof element.animate !== 'function') return;
    this.#played.add(element);

    const image = element.dataset.ovReveal === 'image';
    const requestedDuration = Number(this.dataset.ovMotionDuration);
    const duration = Number.isFinite(requestedDuration) && requestedDuration > 0
      ? Math.min(2400, Math.max(600, requestedDuration))
      : 1400;
    const delay = Math.min(600, Math.max(0, Number(element.dataset.ovRevealDelay) || 0));
    const animation = element.animate([
      { opacity: 0, transform: image ? 'translate3d(0, 40px, 0) scale(.965)' : 'translate3d(0, 40px, 0)' },
      { opacity: 1, transform: 'none' },
    ], { duration, delay, easing: 'cubic-bezier(.22, .61, .36, 1)', fill: 'both' });

    this.#animations.add(animation);
    animation.finished.then(() => {
      this.#animations.delete(animation);
      animation.cancel();
    }, () => this.#animations.delete(animation));
  }

  #stop() {
    this.#observer?.disconnect();
    this.#observer = undefined;
    this.#preference?.removeEventListener('change', this.#onPreferenceChange);
    this.#preference = undefined;
    for (const animation of this.#animations) animation.cancel();
    this.#animations.clear();
  }
}

if (!customElements.get('ovelune-motion')) customElements.define('ovelune-motion', OveluneMotion);
